import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import { ArrowRight, Check, Rocket, Zap, Target, DollarSign, Clock, TrendingUp } from 'lucide-react';
import '@/styles/forms.css';
import '@/styles/landing.css';

const reasons = [
  // "$5,000 consultant" and "for the price of a coffee" both removed 2026-08-17:
  // the first invented a consultant rate we have no source for, the second
  // misdescribes our own pricing ($49 an audit after the first). Qualitative
  // comparison only — do not reintroduce a range without a citation.
  { icon: DollarSign, title: 'No expensive UX consultants', desc: 'Get a structured, evidence-backed audit without a consultant engagement or the wait that comes with one. You get one free audit every month.' },
  { icon: Clock, title: 'Results in under 5 minutes', desc: 'Move fast. Upload your screens and get a full audit before your next standup. Ship fixes in the same sprint.' },
  { icon: Target, title: 'Prioritized by impact', desc: 'Every finding is ranked by effort vs. impact so you know exactly what to fix first with your limited runway.' },
  // WAS: "Baking UX quality in early is 10x cheaper than fixing it after you
  // have 10,000 users complaining." Cut 2026-08-19 — an unsourced multiplier
  // and an unsourced user count in one sentence, item-2 class.
  //
  // The idea traces to Boehm's cost-of-change curve, but that work is about
  // defect cost across software lifecycle phases and says nothing about "10x"
  // against "10,000 users complaining". Citing it would fail the standing test:
  // a citation is not a source unless the cited work says what we claim, about
  // the subject we claim it about.
  { icon: TrendingUp, title: 'Fix before you scale', desc: 'Catch UX problems while changing them is still a small job, rather than after they are load-bearing.' },
];

const stages = [
  { stage: 'Pre-launch', desc: 'Validate your MVP UX before launch day. Catch critical issues while they\'re still cheap to fix.' },
  // Was "Fix onboarding and convert more trials" — benefit language about a
  // flow we cannot audit from a single page. Same rewrite as the /use-cases/saas/
  // hero: what a capture can actually show is friction on the screens.
  // "Understand why users are churning in week 1." cut 2026-08-19 — item 4,
  // near-verbatim. Churn is retention measured over time across a population,
  // and a "week 1" window makes the claim more specific, not less. A capture
  // cannot see either. The signup-friction half is what a capture can show and
  // it stays.
  { stage: 'Early traction', desc: 'Fix the friction on your signup screens and convert more trials to paying customers.' },
  { stage: 'Series A prep', desc: 'Polish the product UX before investor demos and due diligence. First impressions matter enormously.' },
  { stage: 'Post-product-market fit', desc: 'Once you\'ve found PMF, optimize the UX to reduce CAC and increase LTV through better conversion design.' },
];

export default function UseCasesStartupsPage() {
  const { login, user } = useAuth();

  return (
    <div>
      <SEO
        fullTitle="UX Audit for Startups: Validate UX Before You Scale"
        description="Launch confidently. Helps early-stage startups identify critical UX issues before they hurt retention. Fast, affordable AI audits — no UX team required."
        canonical="/use-cases/startups"
        ogTitle="UX Audits for Startups — Validate UX Fast | UXAuditPro"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'UX Audits for Startups',
          url: 'https://www.uxauditpro.com/use-cases/startups/',
          description: 'AI-powered UX audits for startups and founders. Affordable, fast, and actionable — fix UX issues before they hurt growth.',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.uxauditpro.com/' },
              { '@type': 'ListItem', position: 2, name: 'Use Cases', item: 'https://www.uxauditpro.com/use-cases/' },
              { '@type': 'ListItem', position: 3, name: 'Startups', item: 'https://www.uxauditpro.com/use-cases/startups/' },
            ],
          },
        }}
      />

      {/* Hero */}
      <header className="uxa-pt-xl px-6 hero" style={{ position: 'relative', textAlign: 'center' }}>
        <div className="uxa-hero-grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto' }}>
          <span className="section-label inline-block mb-6">Startups & Founders</span>
          <h1
            className="uxa-h1 mb-6"
            style={{ color: 'var(--ink-1)' }}
          >
            Find UX blockers{' '}<br />
            <span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>before your users do</span>
          </h1>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', maxWidth: 620, margin: '20px auto 34px' }}>
            Fix UX problems before they stall your growth. Get expert-quality insights without the consultant price tag — results in under 5 minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Audit My Startup App Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto">
                Audit My Startup App Free
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            <Link to="/case-studies/" className="uxa-submit uxa-submit--auto uxa-submit--secondary">
              See Real Results
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
          <p className="text-sm mt-6" style={{ color: 'var(--ink-2)' }}>
            {/* "Used by 500+ product teams" removed 2026-08-17: production has 17 users. */}
            No credit card · One free audit every month
          </p>
        </div>
      </header>

      {/* Why startups */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          <span className="section-label block text-center mb-4">Built for Startups</span>
          <h2
            className="uxa-h2 text-center mb-16"
            style={{ color: 'var(--ink-1)' }}
          >
            Why startup founders love UXAuditPro
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {reasons.map((item) => (
              <div key={item.title} className="uxa-feature-card" style={{ display: 'flex', gap: 22 }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--accent-tint)' }}>
                  <item.icon className="w-6 h-6" style={{ color: 'var(--accent-text)' }} />
                </div>
                <div>
                  <h3 className="uxa-h3 mb-2" style={{ color: 'var(--ink-1)' }}>{item.title}</h3>
                  <p className="uxa-dense" style={{ color: 'var(--ink-2)' }}>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Startup stages */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          {/* THE PAGE'S ONE TONAL BREAK. It was a sand band, and the closing
              CTA below it was the dark one -- two breaks, where the system's
              pages take one. Same call as /use-cases/: the break goes where the
              process is, and the CTA becomes the light closing band. */}
          <div className="uxa-dark-panel">
            <div style={{ textAlign: 'center' }}>
              <span className="uxa-dark-panel__eyebrow">Every Stage</span>
              <h2 className="uxa-h2" style={{ color: 'var(--text-inverse)', margin: '20px auto 0', maxWidth: 720 }}>
                UX audits at every startup stage
              </h2>
            </div>
            <div className="uxa-dim-grid">
              {stages.map((item, i) => (
                <div key={item.stage} className="uxa-dim-grid__cell">
                  <span style={{ display: 'block', fontSize: 'var(--type-dense-size)', fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent-soft)', marginBottom: 10 }}>{`0${i + 1} — ${item.stage.toUpperCase()}`}</span>
                  <p className="uxa-body" style={{ color: 'var(--ink-4)', margin: 0 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What you get */}
      <section className="uxa-pt-xl">
        <div className="uxa-landing-section">
          <span className="section-label block text-center mb-4">What's Included</span>
          <h2
            className="uxa-h2 text-center mb-16"
            style={{ color: 'var(--ink-1)' }}
          >
            Everything a startup founder needs
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              // "with benchmarks" cut 2026-08-19. NO BENCHMARK CORPUS EXISTS.
              // This is the fourth separate surface of one fabrication: the
              // homepage mockup's "vs SaaS avg 65", /use-cases/saas/'s
              // "Benchmark against SaaS industry averages", the "n=142"
              // sample size, and this. Each was found on its own, months
              // apart. The number guard on the open-items list would have
              // caught all four in one pass — see docs/DECISIONS.md.
              '6 UX dimension scores',
              'Prioritized fix list (effort × impact)',
              'Quick wins you can ship this week',
              'Three-sprint roadmap with effort estimates',
              'Research-backed findings with citations',
              'PDF report ready to share with your team',
              'Competitor comparison (coming soon)',
              'One free audit every month, no credit card needed',
            ].map((item) => (
              <div key={item} className="uxa-feature-card__frame" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <span className="uxa-price-tick" style={{ marginTop: 0, width: 22, height: 22 }} aria-hidden="true"><Check size={12} /></span>
                <span className="text-sm" style={{ color: 'var(--ink-1)' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      {/* ═══ CLOSING CTA ═══════════════════════════════════════════════════════
          Was an --ink-strong band. The page's one tonal break is now the dark
          How It Works panel above, so this takes the homepage's closing shape:
          sand ground, masked 56px grid, spectrum bloom under the primary
          action. The italic accent moved with the ground -- --accent-soft is
          10.21:1 on ink and --accent-text 2.71:1; on sand it reverses, so the
          value swaps and the pair stays AA. */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2
            className="uxa-h2 mb-5"
            style={{ color: 'var(--ink-1)' }}
          >
            Ship a better product today.
          </h2>
          <p
            className="uxa-lead mb-10"
            style={{ color: 'var(--ink-2)', maxWidth: 520, margin: '18px auto 40px' }}
          >
            One free audit every month. No credit card. Results in under 5 minutes.
          </p>
          <div style={{ position: 'relative', display: 'inline-block', isolation: 'isolate' }}>
            <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Start Free Startup Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto">
                Start Free Startup Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
